import React, { useContext, useEffect } from 'react'
import { motion } from 'framer-motion/dist/framer-motion'
import parse, { domToReact } from 'html-react-parser'

import { UIContext } from '../../../Context/UIContext'

import './Awards.scss'

import Section from '../../Section'
import MotionModal from '../../Modal'
import Award from './Award'

const options = {
    replace: ({ attribs, children }) => {
        if (!attribs) return;

        const tagNames = ['h1', 'h2', 'h3', 'p', 'ul'];
        const tagName = attribs.tagName;

        if (tagNames.includes(tagName)) {
            if (tagName === 'ul') {
                return (
                    <ul>
                        {children.map((child, i) => 
                            <li key={`sub-item-${i}`}>
                                {domToReact(child.children, options)}
                            </li>
                        )}
                    </ul>
                );
            } else {
                return React.createElement(tagName, {}, domToReact(children, options));
            }
        }
    }
}

const container = {
    hidden: { opacity: 0 },
    show: {
        opacity: 1,
        transition: {
            duration: 0.5,
            staggerChildren: 0.5,
            delayChildren: 0.5,
        }
    }
}

const drawWave = (color) => {
    return {
        hidden: () => { 
            return {
                pathLength: 0,
                fill: '#FFFFFF',
                fillOpacity: 0,
                stroke: '#FFFFFF',
                opacity: 0
            }
        },
        visible: (i) => {
            let stroke = color
            const strokeDelay = i * 0.5
            const fillDelay = strokeDelay + 0.25
            const transition = {
                pathLength: { delay: strokeDelay, duration: 2, ease: "easeInOut" },
                opacity: { delay: strokeDelay, duration: 0.1 },
                fillOpacity: { delay: fillDelay, duration: 1.5, ease: "easeInOut"},
                fill: { delay: fillDelay, duration: 1.5, ease: "easeInOut"},
            }
            return {
                pathLength: 1.1,
                fill: color,
                fillOpacity: 1,
                stroke,
                opacity: 1,
                pathOffset: 0,
                transition,
            }
        },
    }
}

const Awards = ({ data }) => {
    const { title, content } = data

    const { modalVisible, setSelectedModal } = useContext(UIContext)

    useEffect(() => {
        if (!modalVisible) {
            setSelectedModal(null)
        }
    }, [modalVisible, setSelectedModal])

    return (
        <>
            <Section
                name="mga-awards"
                className="mga-awards-section"
                motionProps={{
                    initial: { opacity: 0 },
                    animate: { opacity: 1 },
                    exit: { opacity: 0 },
                    transition: { duration: 0.5 }
                }}
            >
                <div className="mga-awards-section__wrapper">
                    <motion.h1
                        className="mga-awards-section__title"
                        initial={{ opacity: 0, x: '-50%' }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 1, type: 'tween' }}
                    >
                        { title }
                    </motion.h1>
                    <motion.div
                        className="mga-awards-section__intro"
                        initial={{ opacity: 0, y: '100%' }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1, delay: 0.5 }}
                    >
                        { parse(content.intro, options) }
                    </motion.div>
                </div>
                {content.awards &&
                    <motion.div
                        className="mga-awards-section__grid"
                        initial="hidden"
                        variants={container}
                        animate="show"
                    >

                        <motion.svg
                            className="mga-awards-section__bg"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 1274 229"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.1 }}
                            style={{ 
                                fillRule: "evenodd", 
                                clipRule: "evenodd", 
                                strokeLinejoin: "round", 
                                strokeMiterlimit: 2,
                                strokeWidth: 0.2
                            }}
                        >
                            <motion.path
                                d="M331.578 107.455a2662.601 2662.601 0 0 1 90.773-24.252 2764.614 2764.614 0 0 1 93.5-21.494 2844.45 2844.45 0 0 1 96.017-18.589 2910.734 2910.734 0 0 1 98.322-15.542 2905.37 2905.37 0 0 1 99.024-12.184 2831.907 2831.907 0 0 1 97.663-8.6c32.01-2.26 64.321-3.977 96.035-5.1A2664.088 2664.088 0 0 1 1097.055.01c37.056 0 74.248.807 110.544 2.4 22.457.984 44.922 2.283 67.153 3.882v40.054a2241.45 2241.45 0 0 0-53.03-5.318c-34.474-3.043-69.88-5.363-105.231-6.9a2518.137 2518.137 0 0 0-108.54-2.33c-26.7 0-53.924.423-80.9 1.257a2707.51 2707.51 0 0 0-82.27 3.812 2761.439 2761.439 0 0 0-83.489 6.422 2825.128 2825.128 0 0 0-84.558 9.089 2826.499 2826.499 0 0 0-93.172 13.024 2757.42 2757.42 0 0 0-91.1 15.869 2688.295 2688.295 0 0 0-88.844 18.589 2581.246 2581.246 0 0 0-86.392 21.181C205.226 150.567 98.5 186.95.007 229.177c102.418-47.096 213.977-88.045 331.571-121.722z"
                                stroke="#606060"
                                fill="#FFFFFF"
                                variants={drawWave("#606060")}
                                initial="hidden"
                                animate="visible"
                                custom={0}
                                style={{
                                    fillRule: "nonzero",
                                }}    
                            />
                        </motion.svg>
                        {content.awards.map((item, i) =>
                            <Award key={`award-${i}`} data={item} />
                        )}
                    </motion.div>}
            </Section>
            <MotionModal key="awards-global-modal" />
        </>
    )
}

export default React.memo(Awards)